import {
  autoUpdate,
  flip,
  FloatingPortal,
  offset,
  shift,
  useClick,
  useDismiss,
  useFloating,
  useInteractions,
} from '@floating-ui/react';
import { useMutation } from '@tanstack/react-query';
import cn from 'classnames';
import { postRecsysRecommendationFeedbackMutation } from 'client/@tanstack/react-query.gen';
import { IconMessageCircle } from 'components/UI/Icons/MessageCircle';
import { IconThumbsDown } from 'components/UI/Icons/ThumbsDown';
import { IconThumbsUp } from 'components/UI/Icons/ThumbsUp';
import { IconX } from 'components/UI/Icons/X';
import { Button } from 'components/UI/Tailwind/Button';
import { FormTextArea } from 'components/UI/Tailwind/FormTextArea';
import { forwardRef, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import toast from 'react-hot-toast/headless';
import { useTranslation } from 'react-i18next';

export interface SentFeedback {
  verdict: 'good' | 'bad';
  note?: string;
}

type Verdict = SentFeedback['verdict'];

const iconButton =
  'flex h-8 w-8 items-center justify-center rounded-md transition-colors hover:bg-gray-100 disabled:opacity-50 [&_svg]:h-5 [&_svg]:w-5';

// One icon button of the rail/popover set: gray at rest, black when active.
const IconActionButton = forwardRef<
  HTMLButtonElement,
  JSX.IntrinsicElements['button'] & {
    active: boolean;
    label: string;
  }
>(({ active, children, label, ...props }, ref) => (
  <button
    ref={ref}
    aria-label={label}
    aria-pressed={active}
    className={cn(iconButton, active ? 'text-gray-900' : 'text-gray-400')}
    type="button"
    {...props}
  >
    {children}
  </button>
));

// Efficiency-first feedback for the suggestion detail row: thumbs are
// separate one-click buttons that submit immediately (the darkened icon is
// the confirmation — no popover, no success toast); only the note button
// opens a composer. The API requires a verdict with every note, so the
// composer carries its own thumb pair and Send stays disabled until one is
// picked. `sent` re-seeds state after row remounts (rows rebuild whenever
// the query refetches, and RecSys doesn't echo feedback back).
export function FeedbackRail({
  onSent,
  pipelineId,
  recommendationId,
  sent: sentInitial,
  teamId,
}: {
  onSent: (feedback: SentFeedback) => void;
  pipelineId: string;
  recommendationId: string;
  sent?: SentFeedback;
  teamId: string;
}) {
  const { t } = useTranslation();
  const [sentLocal, setSentLocal] = useState<SentFeedback | undefined>();
  const sent = sentLocal ?? sentInitial;

  const [open, setOpenState] = useState(false);
  // Verdict staged inside the composer only; the rail thumbs submit directly.
  const [verdict, setVerdict] = useState<Verdict | null>(null);
  const methods = useForm<{ note: string }>({ defaultValues: { note: '' } });

  const setOpen = (nextOpen: boolean) => {
    if (nextOpen) {
      setVerdict(sent?.verdict ?? null);
      methods.reset({ note: sent?.note ?? '' });
    }
    setOpenState(nextOpen);
  };

  const { refs, floatingStyles, context } = useFloating({
    open,
    onOpenChange: setOpen,
    placement: 'bottom-end',
    middleware: [offset(4), flip(), shift()],
    whileElementsMounted: autoUpdate,
  });
  const click = useClick(context);
  const dismiss = useDismiss(context, {
    escapeKey: true,
    outsidePress: true,
    ancestorScroll: true,
  });
  const { getReferenceProps, getFloatingProps } = useInteractions([
    click,
    dismiss,
  ]);

  const feedback = useMutation({
    ...postRecsysRecommendationFeedbackMutation(),
    onError: () =>
      toast.error(
        t('page.teamPipeline.internal.recsys.message.feedbackFailed'),
      ),
  });

  const send = (nextVerdict: Verdict, nextNote?: string) => {
    const trimmedNote = nextNote?.trim() || undefined;
    feedback.mutate(
      {
        path: {
          team_uuid: teamId,
          pipeline_uuid: pipelineId,
          recommendation_id: recommendationId,
        },
        body: { verdict: nextVerdict, note: trimmedNote },
      },
      {
        onSuccess: () => {
          const sentFeedback = { verdict: nextVerdict, note: trimmedNote };
          setSentLocal(sentFeedback);
          onSent(sentFeedback);
          setOpenState(false);
        },
      },
    );
  };

  // One-click verdict; flipping re-sends and keeps an already-sent note.
  const quickVerdict = (nextVerdict: Verdict) => {
    if (feedback.isPending || sent?.verdict === nextVerdict) {
      return;
    }
    send(nextVerdict, sent?.note);
  };

  return (
    <div className="flex flex-col gap-1">
      <IconActionButton
        active={sent?.verdict === 'good'}
        disabled={feedback.isPending}
        label={t('page.teamPipeline.internal.recsys.good')}
        onClick={() => quickVerdict('good')}
      >
        <IconThumbsUp />
      </IconActionButton>
      <IconActionButton
        active={sent?.verdict === 'bad'}
        disabled={feedback.isPending}
        label={t('page.teamPipeline.internal.recsys.bad')}
        onClick={() => quickVerdict('bad')}
      >
        <IconThumbsDown />
      </IconActionButton>
      <IconActionButton
        ref={refs.setReference}
        {...getReferenceProps()}
        active={Boolean(sent?.note)}
        label={t('page.newRecommendations.suggestion.note')}
      >
        <IconMessageCircle />
      </IconActionButton>
      {open && (
        <FloatingPortal>
          <div
            ref={refs.setFloating}
            style={floatingStyles}
            {...getFloatingProps()}
            className="z-50 flex w-[480px] flex-col gap-3 rounded-lg border border-gray-200 bg-white p-4 shadow-lg focus:outline-none"
          >
            <h3 className="flex items-center justify-between text-14/120% font-semibold text-black">
              {t('page.teamPipeline.internal.recsys.feedbackTitle')}
              <button
                onClick={() => setOpenState(false)}
                className="text-gray-500 [&>svg]:h-4 [&>svg]:w-4"
                type="button"
              >
                <IconX />
              </button>
            </h3>
            <div className="flex gap-1">
              <IconActionButton
                active={verdict === 'good'}
                label={t('page.teamPipeline.internal.recsys.good')}
                onClick={() => setVerdict('good')}
              >
                <IconThumbsUp />
              </IconActionButton>
              <IconActionButton
                active={verdict === 'bad'}
                label={t('page.teamPipeline.internal.recsys.bad')}
                onClick={() => setVerdict('bad')}
              >
                <IconThumbsDown />
              </IconActionButton>
            </div>
            <FormProvider {...methods}>
              <form
                className="flex flex-col gap-3"
                onSubmit={methods.handleSubmit(({ note }) => {
                  if (verdict) {
                    send(verdict, note);
                  }
                })}
              >
                <FormTextArea
                  name="note"
                  placeholder={t(
                    'page.teamPipeline.internal.recsys.notePlaceholder',
                  )}
                  rows={4}
                />
                <div className="flex justify-end">
                  <Button
                    disabled={!verdict || feedback.isPending}
                    type="submit"
                    variant="default-primary"
                  >
                    {t('page.teamPipeline.internal.recsys.send')}
                  </Button>
                </div>
              </form>
            </FormProvider>
          </div>
        </FloatingPortal>
      )}
    </div>
  );
}
